import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface Experience {
  id: string;
  title: string;
  slug: string;
  price_from: number;
  category: string;
  group_type: string;
  region: string;
  image: string;
}

interface ExperienceRow extends Experience {
  title_vi: string | null;
  category_vi: string | null;
  group_type_vi: string | null;
  region_vi: string | null;
}

export function useExperiences() {
  const [rows, setRows] = useState<ExperienceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchExperiences() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
          .from('vietnamtravel_experiences')
          .select('*')
          .order('created_at', { ascending: false });

        if (err) throw err;
        if (!cancelled) setRows((data || []) as ExperienceRow[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load experiences');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchExperiences();
    return () => { cancelled = true; };
  }, []);

  const experiences: Experience[] = rows.map((r) => ({
    id: r.id,
    title: loc(language, r.title, r.title_vi),
    slug: r.slug,
    price_from: r.price_from,
    category: loc(language, r.category, r.category_vi),
    group_type: loc(language, r.group_type, r.group_type_vi),
    region: r.region,
    image: r.image,
  }));

  return { experiences, loading, error };
}